import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Wand2, Image as ImageIcon, Loader2, Check, Link as LinkIcon, History } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/hooks/useSiteSettings";

interface HeroImage {
  id: string;
  image_url: string;
  source_type: 'ai_generated' | 'uploaded' | 'url';
  prompt?: string;
  is_active: boolean;
  created_at: string;
}

export const HeroImageManager = () => {
  const { toast } = useToast();
  const { settings, refetch } = useSiteSettings();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [urlInput, setUrlInput] = useState("");
  const [images, setImages] = useState<HeroImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const currentHeroImage = settings.hero_image_url || "";

  // Charger l'historique des images
  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoadingImages(true);
    try {
      const { data, error } = await supabase
        .from('hero_images')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching hero images:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  // Upload manuel d'image
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Erreur",
          description: "Le fichier doit être une image (JPG, PNG, WebP, etc.)",
          variant: "destructive",
        });
        return;
      }

      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erreur",
          description: "L'image ne doit pas dépasser 5MB",
          variant: "destructive",
        });
        return;
      }

      setUploading(true);

      // Générer un nom de fichier unique
      const fileExt = file.name.split('.').pop();
      const fileName = `hero-${Date.now()}.${fileExt}`;
      const filePath = `hero-images/${fileName}`;

      // Upload vers Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('site-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Obtenir l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(filePath);

      // Sauvegarder dans l'historique
      const { data: user } = await supabase.auth.getUser();
      await supabase.from('hero_images').insert({
        image_url: publicUrl,
        source_type: 'uploaded',
        is_active: false,
        created_by: user.user?.id,
      });

      // Mettre à jour les settings
      await updateHeroImage(publicUrl);

      toast({
        title: "Image uploadée avec succès",
        description: "L'image hero a été mise à jour",
      });

      // Rafraîchir la galerie
      fetchImages();

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Erreur d'upload",
        description: error.message || "Impossible d'uploader l'image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Ajouter une image par URL
  const handleAddByUrl = async () => {
    if (!urlInput.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer une URL valide",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      // Sauvegarder dans l'historique
      const { data: user } = await supabase.auth.getUser();
      await supabase.from('hero_images').insert({
        image_url: urlInput,
        source_type: 'url',
        is_active: false,
        created_by: user.user?.id,
      });

      // Mettre à jour les settings
      await updateHeroImage(urlInput);

      toast({
        title: "Image ajoutée avec succès",
        description: "L'image hero a été mise à jour",
      });

      setUrlInput("");
      fetchImages();

    } catch (error: any) {
      console.error('Add URL error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ajouter l'image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Générer une image avec l'IA
  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Prompt requis",
        description: "Veuillez entrer une description pour générer l'image",
        variant: "destructive",
      });
      return;
    }

    try {
      setGenerating(true);

      // Appeler la fonction edge pour générer l'image via Replicate
      const { data, error } = await supabase.functions.invoke('generate-hero-image', {
        body: {
          prompt: aiPrompt,
          aspectRatio: "16:9" // Format paysage pour hero
        }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw error;
      }

      // Check if response contains an error (even if no exception was thrown)
      if (data?.error) {
        toast({
          title: data.error,
          description: data.message || data.instructions?.join('\n') || "Configuration requise",
          variant: "destructive",
        });
        return;
      }

      if (data?.imageUrl) {
        setPreviewUrl(data.imageUrl);
        
        toast({
          title: "Image générée !",
          description: "Prévisualisez l'image et cliquez sur 'Utiliser cette image' pour l'appliquer",
        });
      } else {
        throw new Error('Aucune image retournée par l\'API');
      }

    } catch (error: any) {
      console.error('AI generation error:', error);
      toast({
        title: "Erreur de génération",
        description: error.message || "Impossible de générer l'image avec l'IA",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  // Appliquer l'image générée par IA
  const handleApplyAIImage = async () => {
    if (!previewUrl) return;

    try {
      setUploading(true);

      // Télécharger l'image générée
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      
      // Créer un fichier
      const fileName = `hero-ai-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Upload vers Supabase Storage
      const filePath = `hero-images/${fileName}`;
      const { error: uploadError } = await supabase.storage
        .from('site-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Obtenir l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(filePath);

      // Sauvegarder dans l'historique
      const { data: user } = await supabase.auth.getUser();
      await supabase.from('hero_images').insert({
        image_url: publicUrl,
        source_type: 'ai_generated',
        prompt: aiPrompt,
        is_active: false,
        created_by: user.user?.id,
      });

      // Mettre à jour les settings
      await updateHeroImage(publicUrl);

      setPreviewUrl("");
      setAiPrompt("");

      toast({
        title: "Image appliquée avec succès",
        description: "L'image hero a été mise à jour",
      });

      // Rafraîchir la galerie
      fetchImages();

    } catch (error: any) {
      console.error('Apply AI image error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'appliquer l'image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Activer une image depuis l'historique
  const setImageAsActive = async (imageId: string, imageUrl: string) => {
    try {
      setUploading(true);

      // Mettre à jour is_active dans la table hero_images
      const { error: updateError } = await supabase
        .from('hero_images')
        .update({ is_active: true })
        .eq('id', imageId);

      if (updateError) throw updateError;

      // Mettre à jour les settings
      await updateHeroImage(imageUrl);

      toast({
        title: "Image activée",
        description: "L'image hero a été mise à jour",
      });

      // Rafraîchir la galerie
      fetchImages();

    } catch (error: any) {
      console.error('Set active error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'activer l'image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Mettre à jour l'URL de l'image hero dans les settings
  const updateHeroImage = async (imageUrl: string) => {
    const { error } = await supabase
      .from('site_settings')
      .upsert({
        key: 'hero_image_url',
        value: imageUrl,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });

    if (error) throw error;

    // Rafraîchir les settings
    await refetch();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Gestion de l'Image Hero
        </CardTitle>
        <CardDescription>
          Uploadez une nouvelle image ou générez-en une avec l'IA
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Image actuelle */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Image actuelle</Label>
            <div className="relative aspect-video w-full max-w-2xl rounded-lg overflow-hidden border-2 border-border">
              {currentHeroImage ? (
                <img
                  src={currentHeroImage}
                  alt="Hero actuel"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <p className="text-muted-foreground">Aucune image configurée</p>
                </div>
              )}
            </div>
          </div>

          {/* Tabs: Gallery, Upload, AI, URL */}
          <Tabs defaultValue="gallery" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="gallery" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Galerie
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="ai" className="flex items-center gap-2">
                <Wand2 className="h-4 w-4" />
                IA
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                URL
              </TabsTrigger>
            </TabsList>

            {/* Galerie des 5 dernières images */}
            <TabsContent value="gallery" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Les 5 dernières images utilisées. Cliquez sur une image pour la remettre en avant.
              </p>
              
              {loadingImages ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : images.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {images.map((image) => (
                    <Card key={image.id} className="overflow-hidden group hover:shadow-lg transition-shadow">
                      <div className="relative aspect-video">
                        <img
                          src={image.image_url}
                          alt="Hero"
                          className="w-full h-full object-cover"
                        />
                        {image.is_active && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Active
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          {!image.is_active && (
                            <Button
                              size="sm"
                              onClick={() => setImageAsActive(image.id, image.image_url)}
                              disabled={uploading}
                            >
                              Utiliser cette image
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {image.source_type === 'ai_generated' && (
                            <>
                              <Wand2 className="w-3 h-3" />
                              <span>Générée par IA</span>
                            </>
                          )}
                          {image.source_type === 'uploaded' && (
                            <>
                              <Upload className="w-3 h-3" />
                              <span>Uploadée</span>
                            </>
                          )}
                          {image.source_type === 'url' && (
                            <>
                              <LinkIcon className="w-3 h-3" />
                              <span>Depuis URL</span>
                            </>
                          )}
                        </div>
                        {image.prompt && (
                          <p className="text-xs text-muted-foreground line-clamp-2" title={image.prompt}>
                            "{image.prompt}"
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {new Date(image.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune image dans l'historique</p>
                  <p className="text-xs mt-1">Ajoutez votre première image via les autres onglets</p>
                </div>
              )}
            </TabsContent>

            {/* Upload Manuel */}
            <TabsContent value="upload" className="space-y-4">
              <div>
                <Label htmlFor="hero-upload" className="text-sm font-medium mb-2 block">
                  Choisir une image
                </Label>
                <Input
                  id="hero-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Format recommandé : 1920x1080px (16:9) • Max 5MB • JPG, PNG, WebP
                </p>
              </div>

              {uploading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Upload en cours...
                </div>
              )}
            </TabsContent>

            {/* Ajout par URL */}
            <TabsContent value="url" className="space-y-4">
              <div>
                <Label htmlFor="url-input" className="text-sm font-medium mb-2 block">
                  URL de l'image
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="url-input"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    disabled={uploading}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddByUrl}
                    disabled={uploading || !urlInput.trim()}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Ajouter"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Ajoutez une image depuis une URL externe (doit être accessible publiquement)
                </p>
              </div>
            </TabsContent>

            {/* Génération IA */}
            <TabsContent value="ai" className="space-y-4">
              <div>
                <Label htmlFor="ai-prompt" className="text-sm font-medium mb-2 block">
                  Description de l'image souhaitée
                </Label>
                <Textarea
                  id="ai-prompt"
                  placeholder="Ex: Un marché africain animé à Kinshasa avec des vendeurs souriants et des produits colorés, style photographique moderne, lumière naturelle..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={generating}
                  rows={4}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Décrivez l'image en détail (style, ambiance, éléments présents, etc.)
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAIGeneration}
                  disabled={generating || !aiPrompt.trim()}
                  className="flex items-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4" />
                      Générer l'image
                    </>
                  )}
                </Button>
              </div>

              {/* Prévisualisation de l'image générée */}
              {previewUrl && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Prévisualisation</Label>
                    <div className="relative aspect-video w-full max-w-2xl rounded-lg overflow-hidden border-2 border-primary">
                      <img
                        src={previewUrl}
                        alt="Image générée par IA"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleApplyAIImage}
                      disabled={uploading}
                      className="flex items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Application...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Utiliser cette image
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPreviewUrl("");
                        setAiPrompt("");
                      }}
                      disabled={uploading}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Exemples de prompts */}
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm font-medium mb-2">💡 Exemples de prompts :</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• "Marché africain vibrant à Kinshasa avec des étals de fruits et légumes colorés, lumière dorée du matin, style photographique professionnel"</li>
              <li>• "Centre commercial moderne africain avec des personnes qui font du shopping, ambiance chaleureuse et accueillante, éclairage naturel"</li>
              <li>• "Point de retrait sécurisé avec packages et casiers, design moderne et épuré, couleurs bleu et orange"</li>
              <li>• "E-commerce africain, livraison de colis, personnes souriantes recevant leurs commandes, style illustration moderne"</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
